import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, Signal, TemplateRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  MsalBroadcastService,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType
} from '@azure/msal-browser';
import { BlockUIModule } from 'primeng/blockui';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { FooterComponent } from "./common/components/footer/footer.component";
import { HeaderComponent } from "./common/components/header/header.component";
import { HelpOverlayComponent } from "./common/components/help-overlay/help-overlay.component";
import { LeftSidebarComponent } from "./common/components/left-sidebar/left-sidebar.component";
import { FacilitySelectComponent } from "./common/components/selects/facility-select/facility-select.component";
import { QUORUM_TENANT } from './common/constants/permission.constants.enum';
import { DebugService } from './common/services/debug.service';
import { DowntimeService } from './common/services/downtime.service';
import { HelpDisplayService } from './common/services/help-display.service';
import { HomeService } from './common/services/home.service';
import { InsightsService } from './common/services/insights.service';
import { LoadingService } from './common/services/loading.service';
import { NavigationService } from './common/services/navigation.service';
import { PageActionsService } from './common/services/page-actions.service';
import { ThemeService } from './common/services/theme.service';
import { UserService } from './common/services/user.service';
import { VersionCheckService } from './common/services/version-check.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    CommonModule,
    MsalModule,
    BlockUIModule,
    ProgressSpinnerModule,
    PanelModule,
    ToastModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    LeftSidebarComponent,
    HelpOverlayComponent,
    FacilitySelectComponent
  ],
})
export class AppComponent implements OnInit {
  isIframe = false;
  loading: Signal<boolean>;
  authenticated: Signal<boolean>;
  showHelp: Signal<boolean>;
  isLogoutPage = false;
  pageTitle: Signal<string>;
  showFacility: Signal<boolean>;
  pageActions: Signal<TemplateRef<unknown> | null>;
  private destroyRef = inject(DestroyRef);
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);

  constructor(
    private theme: ThemeService,
    private user: UserService,
    private loader: LoadingService,
    private insights: InsightsService,
    private help: HelpDisplayService,
    private router: Router,
    private downtime: DowntimeService,
    private navService: NavigationService,
    private debug: DebugService,
    private versionCheck: VersionCheckService,
    private home: HomeService,
    private actions: PageActionsService
  ) {
    this.authenticated = this.user.authenticated;
    this.loading = this.loader.loading;
    this.showHelp = this.help.visible;
    this.pageTitle = this.navService.pageTitle;
    this.showFacility = this.navService.showFacility;
    this.pageActions = this.actions.template;
    this.debug.state && console.log(`debug logs ${this.debug.state} for session`);
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(e => {
      const urlNoQuery = e.url.split('?')[0];
      this.isLogoutPage = ['/logged-out', '/not-authorized', '/not-authenticated'].includes(urlNoQuery);
      this.isLogoutPage && this.loader.loading.set(false);
    });
  }

  ngOnInit(): void {
    this.theme.setDarkOrLightTheme('initial');
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED
        )
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/logged-out';
          console.warn('no accounts found - user is logged out already');
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          const payload = result.payload as AccountInfo
          this.authService.instance.setActiveAccount(payload);
        } else {
          const payload = result.payload as AuthenticationResult;
          const account = this.authService.instance.getActiveAccount();
          if (account?.homeAccountId !== payload.account.homeAccountId) {
            this.authService.instance.setActiveAccount(payload.account);
          }
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE)
      )
      .subscribe((result: EventMessage) => {
        if (result.interactionType === InteractionType.Silent) {
          this.authService.acquireTokenPopup({scopes: environment.apiConfig.graphScopes})
          return;
        }
        if (result.error && result.error instanceof BrowserAuthError && result.error.errorCode === 'popup_window_error') {
          this.loader.popupBlocked.set(true);
        }
        else if (result.error && result.error instanceof BrowserAuthError && result.error.errorCode === 'timed_out') {
          this.loader.timedOut.set(true);
        } else {
          if (result.error?.message) {
            const isQuorum = result.error.message.indexOf(QUORUM_TENANT) !== -1;
            if (isQuorum) {
              this.loader.qhcLoginAttempt.set(true);
            } else {
              console.warn('failure: ', result.error.message);
            }
          } else {
            console.warn('failure: ', result);
          }
        }
        this.router.navigate(['/not-authenticated'], {onSameUrlNavigation: 'reload'});
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.INITIALIZE_END)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
    if (activeAccount) {
      this.insights.trackTrace(`${activeAccount.username} was authenticated`);
      this.user.setUserInfo(activeAccount);
      this.downtime.getDowntime();
      this.home.authState.set(true);
    }
  }
}